import { useCallback, useMemo, useRef, useState } from 'react'

import * as React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'

import type { AdultLabel } from 'lib/offer/occupancyUtils'
import { getMaxNonAdultOccupancy, getMaxOccupancies, occupantsExceedIncludedGuests } from 'lib/offer/occupancyUtils'
import OccupancyItemActions from './OccupancyItemActions'
import OccupancyChildAgeSelect from './OccupancyChildAgeSelect'
import OccupancyAdultAgeSelect from './OccupancyAdultAgeSelect'
import { only2AAllowedForLanson } from 'lib/offer/offerSpecificChangesUtils'
import Counter from '../../../../LuxKit/Counter'
import BodyText from '../../../../LuxKit/Typography/BodyText'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import RichTooltip from 'LuxKit/RichTooltip'
import Tooltip from 'LuxKit/Tooltip'
import clsx from 'clsx'
import Group from 'components/utils/Group'
import { countWhere } from 'lib/array/arrayUtils'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import PastPassengerNumberPicker from './PastPassengerNumberPicker/PastPassengerNumberPicker'

const OccupantsContainer = styled(Pane)`
  background-color: transparent;
  flex: 1;
  border-radius: ${props => props.theme.borderRadius.S};
  box-shadow: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    border-radius: 0;
    border: 0;
    padding: ${rem(16)} ${rem(24)};

    &.leftBorder {
      border-inline-start: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    }

    &.smallView {
      border-inline-start: none;
      padding: ${rem(16)} 0 0;
    }
  }
`

const OccupantsTypesContainer = styled.div`
  flex-grow: 1;
  flex-direction: column;
  display: flex;
  flex-wrap: wrap;
  column-gap: ${rem(40)};
  row-gap: ${rem(24)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
  }

  &.vertical-mode {
    flex-direction: column;
  }
`

const OccupantsCountsContainer = styled.div`
  flex-direction: column;
  display: flex;
  flex-wrap: wrap;
  column-gap: ${rem(40)};
  row-gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }
`

const AgeType = styled(Group)`
  flex-grow: 1;
`

const PastPassengerNumberFields = styled(Group)`
  width: 100%;
`

const PastPassengerNumberLoading = styled(Group)`
  align-items: center;
`

const PastPassengerNumberCheckboxSkeleton = styled(LoadingBox)`
  width: ${rem(24)};
  height: ${rem(24)};
`

const PastPassengerNumberLabelSkeleton = styled(LoadingBox)`
  width: ${rem(220)};
  height: ${rem(20)};
`

const PastPassengerNumberToggleLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
`

const OccupantIncrementType = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaQueryUp.tablet} {
    display: inline-block;

    & + & {
      margin-top: 0;
    }

    > * + * {
      margin-top: ${rem(4)};
    }
  }
`

const OccupantsIncrementer = styled(Counter)`
  display: inline-flex;

  ${mediaQueryUp.tablet} {
    margin-top: ${rem(12)};
  }
`

const ItemWrapper = styled(Pane)`
  box-shadow: none;
  background-color: transparent;
  padding: 0 ${rem(20)} ${rem(24)};

  ${mediaQueryUp.tablet} {
    display: flex;
    flex-wrap: wrap;
    padding: 0;
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-radius: ${props => props.theme.borderRadius.S};

    &.smallView {
      display: grid;
      border: none;
      border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

      &:first-child {
        border-top: none;
      }
    }
  }
`

const ExtraGuestsNotice = styled.div`
  /* offsetParent for the inline tooltip; without it Floating UI mispositions inside the transformed modal sheet. */
  position: relative;
  width: 100%;
  margin-top: ${rem(12)};
  padding-top: ${rem(12)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  ${mediaQueryUp.tablet} {
    margin-top: 0;
    padding: ${rem(12)} ${rem(16)} ${rem(16)};
  }
`

// The tooltip trigger's content: inline-flex keeps it content-width so the tooltip
// centres on the text, and cursor:default overrides RichTooltip's pointer cursor.
const NoticeContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(8)};
  cursor: default;
`

// Anchors the tooltip arrow to the icon (the trigger spans icon + text).
const IconAnchor = styled.span`
  display: inline-flex;
  line-height: 0;
`

const TitleSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${rem(16)} 0;

  ${mediaQueryUp.tablet} {
    align-items: flex-start;
    padding: ${rem(16)};

    &:not(.smallView) {
      min-width: ${rem(130)};
      justify-content: center;
    }

    &.smallView {
      padding: ${rem(16)} 0 0;
      align-items: center;
    }
  }
`

const MobileActions = styled(OccupancyItemActions)`
  ${mediaQueryUp.tablet} {
    display: none;
    &.smallView {
      display: flex;
    }
  }
`

const DesktopActions = styled(OccupancyItemActions)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    margin-inline-start: auto;
    justify-self: flex-end;
    align-self: start;
    &.smallView {
      display: none;
    }
  }
`

const DEFAULT_OCCUPANCY: App.Occupants = {
  adults: 1,
  infants: 0,
  children: 0,
  teenagers: 0,
  childrenAge: [],
  childrenBirthDate: [],
}

export type OccupancyChildVariant = 'none' | 'children-only' | 'date-of-birth' | 'child-age' | 'infants'

type PastPassengerDetails = {
  pastPassengerNumbers: Array<string | null>
  passengersInfo: Array<App.PastPassengerInfo>
}

interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'onReset'> {
  title?: string
  /* occupancy objects don't have an ID so we need to provide one */
  itemId: number
  occupancy: App.Occupants
  name?: string
  maxChildAge?: number
  minChildAge?: number
  maxInfantAge?: number
  minInfantAge?: number
  maxTeenagerAge?: number
  capacities?: Array<App.RoomCapacity>
  onChange: (itemId: number, nextOccupancy: App.Occupants, eventLabel?: string) => void
  onRemove?: (itemId: number) => void
  onReset?: (itemId: number) => void
  showRemove?: boolean
  childrenAgeLabel?: string
  smallView?: boolean
  adultLabelType?: AdultLabel
  collectAdultAges?: boolean
  childVariant?: OccupancyChildVariant
  /** If this is false, the reset button will only appear when the control is dirty */
  showResetWhenClean?: boolean
  offerType?: App.OfferType
  offerId?: string
  departureDate?: string
  removeAdultAgeCaption?: boolean
  isAddingExtraGuests?: boolean
  currentRoomOccupancy?: App.Occupants
  /** Disable all "+" buttons to prevent adding more occupants */
  disableAddOccupants?: boolean
  /**
   * Guests included in the room's price, as the allowed per-category combinations.
   * When the selected occupants exceed every combination, a surcharge notice
   * is shown for the room. Omit to disable the notice.
   */
  includedGuests?: Array<App.PackageRoomIncludedGuests>
  allowPastPassengerNumber?: boolean
  isPastPassengerNumberLoading?: boolean
  pastPassengerFieldsRequired?: Array<string>
  personTitlesSupported?: Array<App.Cruises.BookingPersonTitles>
}

function resizePastPassengerDetails(
  occupancy: App.Occupants,
  passengerCount: number,
): PastPassengerDetails {
  return {
    pastPassengerNumbers: Array.from(
      { length: passengerCount },
      (_, index) => occupancy.pastPassengerNumbers?.[index] ?? null,
    ),
    passengersInfo: Array.from(
      { length: passengerCount },
      (_, index) => occupancy.passengersInfo?.[index] ?? {},
    ),
  }
}

function hasPastPassengerDetails(occupancy: App.Occupants): boolean {
  return Boolean(
    occupancy.pastPassengerNumbers?.length ||
    occupancy.passengersInfo?.some((passengerInfo) => Object.keys(passengerInfo).length),
  )
}

function getPassengerCount({
  occupancy,
  infantVariant,
}: {
  occupancy: App.Occupants
  infantVariant: boolean
}): number {
  const childrenAgeCount = occupancy.childrenAge?.length ?? 0
  const nonAdultCount = infantVariant
    ? (occupancy.children ?? 0) + (occupancy.infants ?? 0)
    : Math.max(childrenAgeCount, (occupancy.children ?? 0) + (occupancy.teenagers ?? 0) + (occupancy.infants ?? 0))

  return occupancy.adults + nonAdultCount
}

function OccupancyItem(props: Props) {
  const {
    maxChildAge = 17,
    minChildAge,
    maxInfantAge = -1,
    minInfantAge = 0,
    maxTeenagerAge = -1,
    title,
    itemId,
    occupancy,
    onChange,
    onReset,
    onRemove,
    name,
    capacities,
    showRemove,
    childrenAgeLabel: childrenAgeLabelProp,
    smallView = false,
    adultLabelType = 'adult',
    collectAdultAges = false,
    childVariant = 'child-age',
    showResetWhenClean = false,
    offerType,
    offerId,
    departureDate,
    removeAdultAgeCaption = false,
    isAddingExtraGuests,
    currentRoomOccupancy = DEFAULT_OCCUPANCY,
    disableAddOccupants = false,
    includedGuests,
    allowPastPassengerNumber = false,
    isPastPassengerNumberLoading = false,
    pastPassengerFieldsRequired = [],
    personTitlesSupported = [],
    ...rest
  } = props

  const {
    adults,
    adultAges = [],
    infants = 0,
    children = 0,
    teenagers = 0,
    childrenAge = [],
    childrenBirthDate = [],
    pastPassengerNumbers = [],
    passengersInfo = [],
  } = occupancy

  const childrenRequireDateOfBirth = childVariant === 'date-of-birth'
  const infantVariant = childVariant === 'infants'
  const [isDirty, setDirty] = useState<boolean>(false)
  const extraGuestsIconRef = useRef<HTMLSpanElement>(null)

  const restrictCapacitiesForOffer = offerId && only2AAllowedForLanson(offerId)

  const maxCapacities = useMemo(
    () => getMaxOccupancies(capacities, occupancy, { maxChildAge, maxInfantAge, maxTeenagerAge }),
    [capacities, occupancy, maxChildAge, maxInfantAge, maxTeenagerAge],
  )
  const maxNonAdultOccupancy = useMemo(
    () => getMaxNonAdultOccupancy(capacities, occupancy, { maxChildAge, maxInfantAge, maxTeenagerAge }),
    [capacities, occupancy, maxChildAge, maxInfantAge, maxTeenagerAge],
  )

  const { maxAdults, minAdults, minChildren } = useMemo(() => {
    const calculatedMaxAdults = restrictCapacitiesForOffer ? 2 : maxCapacities.adults
    const calculatedMinAdults = restrictCapacitiesForOffer ? 2 : 1

    if (isAddingExtraGuests) {
      return {
        maxAdults: calculatedMaxAdults,
        minAdults: currentRoomOccupancy.adults,
        minChildren: (currentRoomOccupancy.teenagers ?? 0) + (currentRoomOccupancy.children ?? 0) + (currentRoomOccupancy.infants ?? 0),
      }
    }

    return { maxAdults: calculatedMaxAdults, minAdults: calculatedMinAdults, minChildren: 0 }
  }, [restrictCapacitiesForOffer, maxCapacities.adults, isAddingExtraGuests, currentRoomOccupancy.adults, currentRoomOccupancy.teenagers, currentRoomOccupancy.children, currentRoomOccupancy.infants])

  const onAdultChange = useCallback((val: number) => {
    setDirty(true)
    const newOccupancies: App.Occupants = {
      ...occupancy,
      adults: val,
      ...(collectAdultAges && { adultAges: Array.from({ length: val }, (_, index) => adultAges[index] ?? -1) }),
    }
    if (hasPastPassengerDetails(occupancy)) {
      Object.assign(newOccupancies, resizePastPassengerDetails(
        occupancy,
        getPassengerCount({ occupancy: newOccupancies, infantVariant }),
      ))
    }
    onChange(itemId, newOccupancies, val > occupancy.adults ? 'add-adult' : undefined)
  }, [occupancy, onChange, itemId, infantVariant, collectAdultAges, adultAges])

  const onAdultAgeChange = useCallback((adultId: number, age: number) => {
    setDirty(true)
    const nextAdultAges = Array.from({ length: adults }, (_, index) => adultAges[index] ?? -1)
    nextAdultAges[adultId] = age
    onChange(itemId, { ...occupancy, adultAges: nextAdultAges })
  }, [adults, adultAges, itemId, occupancy, onChange])

  const onChildrenChange = useCallback((val: number) => {
    setDirty(true)

    // For infant variant, directly update children count
    if (infantVariant) {
      const newOccupancies: App.Occupants = {
        ...occupancy,
        children: val,
      }
      if (hasPastPassengerDetails(occupancy)) {
        Object.assign(newOccupancies, resizePastPassengerDetails(
          occupancy,
          getPassengerCount({ occupancy: newOccupancies, infantVariant }),
        ))
      }
      onChange(itemId, newOccupancies, val > (occupancy.children ?? 0) ? 'add-kid' : undefined)
      return
    }

    const nextChildrenAges = [...childrenAge]
    const nextChildrenBirthDate = [...childrenBirthDate]
    const isIncrementing = val > nextChildrenAges.length

    if (isIncrementing) {
      nextChildrenAges.push(-1)
      if (childrenRequireDateOfBirth) nextChildrenBirthDate.push(undefined)
    } else {
      nextChildrenAges.pop()
      if (childrenRequireDateOfBirth) nextChildrenBirthDate.pop()
    }

    const teenagers = countWhere(nextChildrenAges, (age) => age > maxChildAge && age <= maxTeenagerAge)
    const infants = countWhere(nextChildrenAges, (age) => age >= minInfantAge && age <= maxInfantAge)
    const children = nextChildrenAges.length - teenagers - infants

    const newOccupancies: App.Occupants = {
      ...occupancy,
      teenagers,
      children,
      infants,
      childrenAge: nextChildrenAges,
      childrenBirthDate: nextChildrenBirthDate,
    }
    if (hasPastPassengerDetails(occupancy)) {
      Object.assign(newOccupancies, resizePastPassengerDetails(
        occupancy,
        getPassengerCount({ occupancy: newOccupancies, infantVariant }),
      ))
    }

    onChange(itemId, newOccupancies, isIncrementing ? 'add-kid' : undefined)
  }, [childrenAge, onChange, itemId, occupancy, minInfantAge, maxInfantAge, maxChildAge, maxTeenagerAge, childrenBirthDate, childrenRequireDateOfBirth, infantVariant])

  const onChildrenAgeChange = useCallback((childId: number, age: number, birthDate?: string) => {
    setDirty(true)
    const nextChildrenAges = [...childrenAge]
    nextChildrenAges[childId] = age

    const teenagers = countWhere(nextChildrenAges, (age) => age > maxChildAge && age <= maxTeenagerAge)
    const infants = countWhere(nextChildrenAges, (age) => age >= minInfantAge && age <= maxInfantAge)
    const children = nextChildrenAges.length - teenagers - infants

    const nextChildrenBirthDate = [...childrenBirthDate]
    if (childrenRequireDateOfBirth) {
      nextChildrenBirthDate[childId] = birthDate
    }

    const newOccupancy = {
      ...occupancy,
      children,
      teenagers,
      infants,
      childrenAge: nextChildrenAges,
      childrenBirthDate: nextChildrenBirthDate,
    }
    onChange(itemId, newOccupancy)
  }, [childrenAge, occupancy, onChange, itemId, minInfantAge, maxInfantAge, maxChildAge, maxTeenagerAge, childrenBirthDate, childrenRequireDateOfBirth])

  const onResetClick = useCallback(() => {
    setDirty(false)
    onReset?.(itemId)
  }, [onReset, itemId])

  const onRemoveClick = useCallback(() => {
    onRemove?.(itemId)
  }, [onRemove, itemId])

  const onInfantsChange = useCallback((val: number) => {
    setDirty(true)
    const isIncrementing = val > (infants || 0)

    const newOccupancies: App.Occupants = {
      ...occupancy,
      infants: val,
    }
    if (hasPastPassengerDetails(occupancy)) {
      Object.assign(newOccupancies, resizePastPassengerDetails(
        occupancy,
        getPassengerCount({ occupancy: newOccupancies, infantVariant }),
      ))
    }

    onChange(itemId, newOccupancies, isIncrementing ? 'add-kid' : undefined)
  }, [infants, itemId, occupancy, onChange, infantVariant])

  const onPastPassengerDetailsSave = useCallback((index: number, details: {
    pastPassengerNumber: string
    passengerInfo: App.PastPassengerInfo
  }) => {
    setDirty(true)
    const nextPastPassengerDetails = resizePastPassengerDetails(
      occupancy,
      getPassengerCount({ occupancy, infantVariant }),
    )
    nextPastPassengerDetails.pastPassengerNumbers[index] = details.pastPassengerNumber
    nextPastPassengerDetails.passengersInfo[index] = details.passengerInfo

    onChange(itemId, {
      ...occupancy,
      ...nextPastPassengerDetails,
      pastPassengerNumberEnabled: true,
    })
  }, [itemId, occupancy, onChange, infantVariant])

  const onPastPassengerDetailsRemove = useCallback((index: number) => {
    setDirty(true)
    const nextPastPassengerDetails = resizePastPassengerDetails(
      occupancy,
      getPassengerCount({ occupancy, infantVariant }),
    )
    nextPastPassengerDetails.pastPassengerNumbers[index] = null
    nextPastPassengerDetails.passengersInfo[index] = {}

    onChange(itemId, {
      ...occupancy,
      ...nextPastPassengerDetails,
    })
  }, [itemId, occupancy, onChange, infantVariant])

  const onPastPassengerNumberToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDirty(true)
    onChange(itemId, {
      ...occupancy,
      pastPassengerNumberEnabled: event.currentTarget.checked,
    })
  }, [itemId, occupancy, onChange])

  const onlyInfantCanBeAdded = maxCapacities.children === 0 && maxChildAge === 0 && (maxCapacities.infants ?? 0) > 0
  const maxNonAdultAge = onlyInfantCanBeAdded ? maxInfantAge : Math.max(maxTeenagerAge, maxChildAge)
  const minAdultAge = maxNonAdultAge + 1

  const intl = useIntl()
  const childrenAgeLabel = childrenAgeLabelProp ?? intl.formatMessage({
    id: 'GBbQFyyy2xTvJgjpJfV6',
    defaultMessage: 'Age at time of check-in',
    description: 'Default label for children age selection in occupancy picker',
  })
  const adultLabel = useMemo(() => {
    return adultLabelType === 'adult'
      ? intl.formatMessage({
          id: 'rMBsb-bhqwwlYURUsfxx',
          defaultMessage: 'Adults',
          description: 'Label for adults in occupancy picker in the occupancy or guest picker',
        })
      : intl.formatMessage({
          id: 'P1pNQ5h1cPEqNAbC2zwb',
          defaultMessage: 'Travellers',
          description: 'Label for travellers in occupancy picker in the traveller form',
        })
  }, [adultLabelType, intl])

  const childLabel = useMemo(() => {
    return infantVariant
      ? intl.formatMessage({
          id: 'PK_eCBi9jqiiWtztdd-t',
          defaultMessage: 'Children',
          description: 'Label for children in occupancy picker in the occupancy or guest picker',
        })
      : intl.formatMessage({
          id: 'sp0IeoKDD5DW9cS2pmOQ',
          defaultMessage: 'Child',
          description: 'Label for child in occupancy picker in the occupancy or guest picker',
        })
  }, [infantVariant, intl])

  const childrenValue = infantVariant ? children : Math.max(childrenAge.length, children)
  const childrenMaxCapacity = infantVariant
    ? maxCapacities.children
    : maxNonAdultOccupancy
  const childrenMax = disableAddOccupants ? childrenValue : childrenMaxCapacity

  const infantsMaxCapacity = maxCapacities.infants ?? 0
  const infantsMax = disableAddOccupants ? infants : infantsMaxCapacity
  const isPastPassengerNumberEnabled = occupancy.pastPassengerNumberEnabled ?? hasPastPassengerDetails(occupancy)
  // Compared per category, not as a flattened total: 3 adults exceeds a {2 adults, 1 child} allowance.
  const showExtraGuestsNotice = !!includedGuests?.length &&
    occupantsExceedIncludedGuests({ adults, children, teenagers, infants }, includedGuests)

  return (
    <ItemWrapper className={clsx('occupancy-picker-item', { smallView })} data-testid={`occupancy-item-${itemId}`} {...rest}>
      {title &&
        <TitleSection className={clsx({ smallView })}>
          <BodyText variant="medium" weight="bold" format="titlecase">{title}</BodyText>
          <MobileActions
            className={clsx({ smallView })}
            showReset={isDirty && !!onReset}
            showRemove={showRemove}
            onReset={onResetClick}
            onRemove={onRemoveClick}
          />
        </TitleSection>}
      <OccupantsContainer className={clsx('occupancy-picker-item', { smallView, leftBorder: !!title })}>
        <OccupantsTypesContainer className={clsx({ 'vertical-mode': childrenRequireDateOfBirth })}>
          <OccupantsCountsContainer>
            <OccupantIncrementType>
              <label>
                <BodyText variant="medium">{adultLabel}</BodyText>
                {!removeAdultAgeCaption &&
                  <BodyText variant="medium" colour="light-foreground-alt">
                    {intl.formatMessage(
                      {
                        id: 'g689XJeyJClJpeNtnfPy',
                        defaultMessage: 'Ages {age} and above',
                        description: 'Minimum age requirement label for adults in occupancy picker',
                      },
                      { age: minAdultAge },
                    )}
                  </BodyText>}
              </label>
              <OccupantsIncrementer
                data-testid={`occupancy-item-${itemId}-adults`}
                value={adults}
                onChange={onAdultChange}
                max={disableAddOccupants ? adults : maxAdults}
                min={minAdults}
                name={`${name}.adults`}
              />
            </OccupantIncrementType>
            {!restrictCapacitiesForOffer && childVariant !== 'none' && <OccupantIncrementType>
              <label>
                <BodyText variant="medium">{childLabel}</BodyText>
                <BodyText variant="medium" colour="light-foreground-alt">
                  {intl.formatMessage(
                    {
                      id: 'w5XLzM9s9NjMlplZBbgp',
                      defaultMessage: 'Up to {age} years old',
                      description: 'Maximum age requirement label for children in occupancy picker',
                    },
                    { age: maxNonAdultAge },
                  )}
                </BodyText>
              </label>
              <OccupantsIncrementer
                data-testid={`occupancy-item-${itemId}-children`}
                value={childrenValue}
                onChange={onChildrenChange}
                max={childrenMax}
                min={minChildren}
                name={`${name}.children`}
              />
            </OccupantIncrementType>}
            {infantVariant && <OccupantIncrementType>
              <label>
                <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'hy6zHnvXPBLDK3T9e2OX',
                    defaultMessage: 'Infants',
                    description: 'Label for infants in occupancy picker in the occupancy or guest picker',
                  })}
                </BodyText>
                <BodyText variant="medium" colour="light-foreground-alt">
                  {intl.formatMessage({
                    id: 'eVroK4jmHYGq5VeLjpQm',
                    defaultMessage: 'Under 2 years old',
                    description: 'Age requirement label for infants in occupancy picker',
                  })}
                </BodyText>
              </label>
              <OccupantsIncrementer
                data-testid={`occupancy-item-${itemId}-infant`}
                value={infants}
                onChange={onInfantsChange}
                max={infantsMax}
                min={0}
                name={`${name}.infants`}
              />
            </OccupantIncrementType>}
          </OccupantsCountsContainer>
          {collectAdultAges && <AgeType direction="vertical" tabletDirection="horizontal" gap={24} wrap="wrap">
            {Array.from({ length: adults }, (_, index) => <OccupancyAdultAgeSelect
              key={index}
              adultId={index}
              age={adultAges[index]}
              minAge={minAdultAge}
              name={`${name}.adultAges`}
              onChange={onAdultAgeChange}
            />)}
          </AgeType>}
          {childVariant !== 'children-only' && childrenAge.length > 0 &&
            <AgeType direction="vertical" tabletDirection="horizontal" gap={24} wrap="wrap">
              {childrenAge.map((child, index) => <OccupancyChildAgeSelect
                key={index}
                capacities={capacities}
                occupancy={occupancy}
                maxChildAge={maxChildAge}
                minChildAge={minChildAge}
                maxInfantAge={maxInfantAge}
                minInfantAge={minInfantAge}
                maxTeenagerAge={maxTeenagerAge}
                childId={index}
                defaultValue={child === -1 ? undefined : child}
                onChange={onChildrenAgeChange}
                data-testid={`occupancy-item-${itemId}-childrenAge-${index}`}
                name={`${name}.childrenAge[${index}]::number`}
                childrenRequireDateOfBirth={childrenRequireDateOfBirth}
                departureDate={departureDate}
                childrenAgeLabel={childrenAgeLabel}
                required
              />)}
            </AgeType>}
          {isPastPassengerNumberLoading && <PastPassengerNumberLoading
            data-testid={`occupancy-item-${itemId}-past-passenger-number-skeleton`}
            direction="horizontal"
            verticalAlign="center"
            gap={12}
          >
            <PastPassengerNumberCheckboxSkeleton />
            <PastPassengerNumberLabelSkeleton />
          </PastPassengerNumberLoading>}
          {allowPastPassengerNumber && <PastPassengerNumberFields direction="vertical" gap={16}>
            <CheckboxInput
              checked={isPastPassengerNumberEnabled}
              onChange={onPastPassengerNumberToggle}
              data-testid={`occupancy-item-${itemId}-past-passenger-number-toggle`}
            >
              <PastPassengerNumberToggleLabel>
                {intl.formatMessage({
                  id: 'HHmFp5d80Pbbvev-VAHx',
                  defaultMessage: 'Add past passenger number',
                  description: 'Checkbox label to reveal cruise past passenger number inputs in occupancy picker',
                })}
                <Tooltip
                  placement="top"
                  description={intl.formatMessage({
                    id: '4zOLjXCaw6lUx8_V7jLm',
                    defaultMessage: 'You can look this up after entering passenger details at checkout.',
                    description: 'Tooltip explaining when cruise past passenger number lookup is available',
                  })}
                >
                  <LineInfoCircleIcon size="XS" colour="light-foreground-alt" />
                </Tooltip>
              </PastPassengerNumberToggleLabel>
            </CheckboxInput>
            {isPastPassengerNumberEnabled && <PastPassengerNumberPicker
              itemId={itemId}
              passengerCount={getPassengerCount({ occupancy, infantVariant })}
              pastPassengerNumbers={pastPassengerNumbers}
              passengersInfo={passengersInfo}
              pastPassengerFieldsRequired={pastPassengerFieldsRequired}
              personTitlesSupported={personTitlesSupported}
              onSave={onPastPassengerDetailsSave}
              onRemove={onPastPassengerDetailsRemove}
            />}
          </PastPassengerNumberFields>}
        </OccupantsTypesContainer>
        <DesktopActions
          className={clsx({ smallView })}
          showReset={(isDirty || showResetWhenClean) && !!onReset}
          showRemove={showRemove}
          onReset={onResetClick}
          onRemove={onRemoveClick}
        />
      </OccupantsContainer>
      {showExtraGuestsNotice &&
        <ExtraGuestsNotice>
          <RichTooltip
            showOnHover
            dismissible={false}
            variant="dark"
            placement="top"
            anchorRef={extraGuestsIconRef}
            description={intl.formatMessage({
              id: 'MsBt-xhtKsDihpU817uy',
              defaultMessage: 'The number of guests selected exceeds this room\'s standard occupancy. You may continue with your booking, but additional charges may apply when you select your travel dates.',
              description: 'Tooltip shown when hovering or tapping the extra-guests notice in the occupancy picker, explaining surcharges may apply when the customer selects their travel dates',
            })}
          >
            <NoticeContent>
              <IconAnchor ref={extraGuestsIconRef}>
                <LineInfoCircleIcon size="XS" colour="light-foreground-alt" aria-hidden />
              </IconAnchor>
              <BodyText variant="small" colour="light-foreground-alt">
                {intl.formatMessage({
                  id: 'nCkkI_4iNhFvk0Nr43Va',
                  defaultMessage: 'Surcharges may apply due to extra guests in this room',
                  description: 'Per-room notice in the occupancy picker shown when the selected guests exceed the room’s included occupancy',
                })}
              </BodyText>
            </NoticeContent>
          </RichTooltip>
        </ExtraGuestsNotice>}
    </ItemWrapper>
  )
}

export default React.memo(OccupancyItem)
